import throttle from "lodash.throttle";
import {useEffect, useMemo, useRef} from "react";

import {maybeIOS} from "@repo/guide-core-helpers";
import {scrollElementIntoView} from "@repo/util-scrolling";

/**
 * This hook is used to scroll the last message in a conversation into view on iOS.
 * It is a workaround for a bug in iOS Safari where the browser does not scroll
 * to the bottom of the scrollable area as new content stream in.
 *
 * WARNING: This solution isn't SSR safe, but is only used in Khanmigo, which requires a
 * logged-in user, and isn't rendered server-side.
 *
 * @param anchorRef
 * @param message
 * @param isOutgoing
 * @param isIncoming
 * @param isLastMessageInConversation
 */
export const useIOSScrollHack = (
    anchorRef,
    message,
    isOutgoing,
    isIncoming,
    isLastMessageInConversation,
) => {
    const lastMessageTextRef = useRef();
    const throttledScroll = useMemo(
        () =>
            throttle(
                () => {
                    // This combination of duration and throttling seems to provide a
                    // good balance between responsiveness and visual noise.
                    scrollElementIntoView(anchorRef.current, null, {
                        duration: 75,
                    });
                },
                300,
                {leading: true},
            ),
        [anchorRef],
    );

    useEffect(() => {
        // Don't do any of this for non-iOS devices.
        if (!maybeIOS()) {
            return;
        }
        // Don't scroll if this isn't the final message.
        if (!isLastMessageInConversation) {
            return;
        }
        // Outgoing message text (entered by the user) is immediately complete. Scroll.
        // We give this a bit longer duration to balance it with the throttled scroll
        // on the incoming messages.
        if (isOutgoing) {
            scrollElementIntoView(anchorRef.current, null, {duration: 300});
            return;
        }
        // This is the message we care about, but the text is still changing,
        // so we use our throttled scroll to intermittently scroll the existing
        // message text into view, otherwise repeatedly scrolling causes
        // excessive visual noise.
        if (lastMessageTextRef.current !== message.text) {
            lastMessageTextRef.current = message.text;
            throttledScroll();
            return;
        }
    }, [
        isLastMessageInConversation,
        isIncoming,
        isOutgoing,
        message,
        anchorRef,
        throttledScroll,
    ]);
};
